<script lang="ts" setup>
import { watch, type PropType, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';

import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';

import AppHeader from './app-header/AppHeader.vue';

export type ShouldExecuteRedirect = (showLeftNav: boolean) => {
  doIt: boolean;
  route: {
    name: string;
    params?: Dictionary<string>;
    hash?: string;
  };
};
const props = defineProps({
  /** Callback to be executed on route change */
  onRouteChange: { type: Function as PropType<(mainEl: HTMLElement) => void>, required: false },
  /** Condition to show left nav */
  showLeftNav: { type: Boolean, required: true },
  /** Condition if redirect to default route is required */
  shouldExecuteRedirect: { type: Function as PropType<ShouldExecuteRedirect>, required: false },
});

const { isXS } = useMQBreakpoints();
const route = useRoute();
const router = useRouter();

const mainEl = ref<HTMLElement>();

watch(
  () => props.showLeftNav,
  () => {
    if (!props.shouldExecuteRedirect) {
      return;
    }

    const { doIt, route: routeObj } = props.shouldExecuteRedirect(props.showLeftNav);

    if (doIt) {
      router.push(routeObj);
    }
  },
  { immediate: true },
);

watch(route, () => {
  if ((props.onRouteChange ?? props.onRouteChange === 'function') && mainEl.value) {
    props.onRouteChange(mainEl.value);
  }
});
</script>

<template>
  <div class="page-with-side-bar">
    <app-header v-if="!isXS" />
    <div class="page-with-side-bar__content">
      <ot-module-container
        v-if="showLeftNav"
        class="page-with-side-bar__left-nav"
        :with-bottom-padding="true"
      >
        <template #main-content>
          <slot name="leftNav" />
        </template>
      </ot-module-container>
      <div
        ref="mainEl"
        class="page-with-side-bar__main"
      >
        <slot
          name="default"
          :with-left-nav="showLeftNav"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.page-with-side-bar {
  --page-with-side-bar-background-color: rgb(var(--v-theme-elevation-0));

  display: flex;
  height: 100%;
  flex-direction: column;
  background-color: var(--page-with-side-bar-background-color);

  &__content {
    display: flex;
    height: 100%;
    flex-direction: row;
  }

  &__left-nav {
    width: 300px;
    flex: 0 0 300px;
  }

  &__main {
    display: flex;
    overflow: hidden auto;
    max-width: 1300px;
    flex: 1 1 auto;
    flex-direction: column;
    padding: 0 var(--padding-x) var(--zendesk-safe-zone-bottom);
    color: rgb(var(--v-theme-text-primary));

    .xs & {
      padding: 0 var(--padding-x-xs) var(--zendesk-safe-zone-bottom);
    }
  }
}
</style>
